import { createContext, useContext } from "react";
import type { DashboardTabId } from "./dashboardTabs";

/**
 * What every panel of the property dashboard needs to know about the page:
 * which property, who's looking, and how to move around. Provided once by
 * PropertyDetailPage so the tabs don't prop-drill it.
 */
export interface PropertyDashboardValue {
  /** REAPI property id (URLs, public endpoints). */
  propertyId: string;
  /** Mongo _id — what the owner-scoped APIs (contracts, maintenance) take. */
  propertyRefID: string;
  isAuthenticated: boolean;
  /** The viewer is this home's approved owner. */
  isOwner: boolean;
  /** Someone (anyone) has claimed this home. */
  isClaimed: boolean;
  /** The viewer's own claim status: PENDING / APPROVED / REJECTED / ... or null. */
  apnStatus: string | null;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  latitude?: number;
  longitude?: number;
  /** Switch the middle column; `focus` asks the tab to open/scroll to something. */
  goTo: (tab: DashboardTabId, focus?: string) => void;
  /** The claim flow (sign-in first if needed). */
  requestClaim: () => void;
  requestSignIn: () => void;
}

const PropertyDashboardContext = createContext<PropertyDashboardValue | null>(null);

export const PropertyDashboardProvider = PropertyDashboardContext.Provider;

export const usePropertyDashboard = (): PropertyDashboardValue => {
  const ctx = useContext(PropertyDashboardContext);
  if (!ctx) throw new Error("usePropertyDashboard must be used inside PropertyDashboardProvider");
  return ctx;
};

/** A focus request for the active tab: what to open, and a nonce so repeats still fire. */
export interface DashboardFocus {
  target: string;
  nonce: number;
}

export default PropertyDashboardContext;
